import { ApprovalRequestApprover } from './approval-request-approver';
import { AllowanceOptionSelected } from './allowances.model';
import { BookingDates } from './booking-dates.model';
import { ConfirmationGuest } from './confirmation-guest.model';
import { ConfirmationRoom } from './confirmation-room.model';
import { PaymentSummaryCommissionModel } from './payment-summary-commissions.model';
import { TaxesAndFeesPriceBreakdown } from './taxes-and-fees-price-breakdown.model';

export interface ReservationViewResponse {
  address: string;
  allowanceAllowed: boolean;
  allowanceSelected: AllowanceOptionSelected[];
  checkIn: string;
  checkOut: string;
  clientCommission: PaymentSummaryCommissionModel;
  clientFields: { name: string; value: string; label?: string }[];
  coordinates: {
    longitude: string;
    latitude: string;
    radius: number;
  };
  currency: string;
  customer: {
    address: string;
    email: string;
    name: string;
    phone: string;
  };
  customerNotice: string;
  freeCancellationBefore: string;
  freeCancellationBeforeWithOffset?: string;

  guid: string;
  hotelName: string;
  hotelNotice: string;
  isOnAccount: boolean;
  issued: string;
  phone: string;
  reference: string;
  reservationUkVat: number;
  rooms: ConfirmationRoom[];
  status: string;
  termsAndConditions: string;
  total: number;
  starRating: number;
  thumbnailUrl: string;
  priceBreakdown?: TaxesAndFeesPriceBreakdown;
  approvers?: ApprovalRequestApprover[] | null;
  reason?: string | null;
  completedBySystem?: boolean | null;
}

export enum AmendAPIStatus {
  SUCCESS = 'Success',
  BAD_REQUEST = 'BadRequest',
  SERVER_ERROR = 'ServerError',
}

interface ChangeSet {
  getChangeObj: () => Record<string, unknown>;
}

export class AmendGuestsChange implements ChangeSet {
  constructor(public passengers: ConfirmationGuest[]) {}

  getChangeObj() {
    return { passengers: this.passengers };
  }
}

export interface BookingConfirmationData extends ReservationViewResponse {
  allowance: { name: string; id: number } | null;
  approvalReason?: string;
}

export class BookingConfirmation {
  constructor(private reservationViewResponse: ReservationViewResponse) { }

  getData(): BookingConfirmationData {
    const { clientFields, allowanceSelected } = this.reservationViewResponse;
    // prettier-ignore
    const [allowance] = allowanceSelected ?? [];
    return {
      ...this.reservationViewResponse,
      allowance: allowance ? { name: allowance.value, id: allowance.id } : null,
      clientFields: clientFields.filter((field) => field.name !== 'Allowance'),
    };
  }
}

export interface ModificationPenalty {
  deadline: string;
  description: string;
  currencyCode: string;
  oldAmount: number;
  newAmount: number;
}

interface BaseAmendBookingProposal {
  reservationId: string;
}

export class AmendBookingProposal<T> implements BaseAmendBookingProposal {
  constructor(public reservationId: string, public changeSet: T) {}

  isAmendGuestsChange() {
    return this.changeSet instanceof AmendGuestsChange;
  }
}

export class AmendDatesChange implements ChangeSet {
  constructor(public dates: BookingDates) {}

  getChangeObj() {
    return { dates: this.dates };
  }
}

export interface CheckAmendmentPenaltiesResponse {
  status: AmendAPIStatus;
  modificationPenalties: ModificationPenalty[];
}

export interface AmendmentGuestComparison {
  modifiedGuests: ConfirmationGuest[];
  originalGuests: ConfirmationGuest[];
}
